import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { useDeleteProductAttributeValue, useMeasurementFamily, useSetProductAttributeValue } from '../../hooks';
import { readStringDetails } from '../../lib/apiErrorDetails';
import { formatNumber } from '../../lib/format';
import { resolveLocalizedText } from '../../lib/localizedText';
import type { ToastSeverity } from '../../lib/useToast';
import { ATTRIBUTE_TYPE_LABELS } from '../../model/options';
import type {
  AttributeDefinition,
  AttributeValue,
  AttributeValueSetRequest,
  CatalogLanguage,
} from '../../model/types';
import type { LocalizedFieldValue } from './LocalizedFieldsEditor';

export interface AttributeValueRowProps {
  productId: string;
  definition: AttributeDefinition;
  required: boolean;
  value: AttributeValue | undefined;
  languages: CatalogLanguage[];
  /**
   * The card-language switcher's currently selected locale (adr-merchant-portal-008) — a
   * localizable TEXT attribute renders ONE field, for THIS locale, instead of stacking every
   * configured language at once. Falls back to the tenant's content-default locale when absent or
   * unknown.
   */
  selectedLocale?: string;
  canManage: boolean;
  onNotify: (severity: ToastSeverity, message: string) => void;
}

function valueAsString(value: AttributeValue | undefined): string {
  if (!value || value.value === undefined || value.value === null) return '';
  return typeof value.value === 'string' ? value.value : String(value.value);
}

function sortedJson(values: string[]): string {
  return JSON.stringify([...values].sort());
}

/**
 * One typed attribute value editor row, driven by the assigned family's attribute definition
 * (user-story-merchant-portal-030 slice 2). Save is explicit (per-row, with a dirty indicator),
 * matching the mutation's own one-call-per-attribute shape; Clear issues the DELETE. A
 * `422 ATTRIBUTE_VALIDATION_FAILED` renders inline as this row's field error (rule + expected),
 * not just a toast.
 *
 * **Localizable TEXT** (adr-merchant-portal-008): rendered as ONE field bound to the card-language
 * switcher's `selectedLocale` — the product card never shows two languages' values at once. The
 * per-locale draft still accumulates every locale edited this session (switching the card language
 * preserves in-progress edits), and saving sends `translations` for those locales only.
 *
 * **Localizable TEXT caveat** (documented, not silently assumed): `AttributeValue` (the read
 * model) exposes only the value resolved for ONE locale (`resolvedLocale`) — there is no
 * `GET`-with-all-translations shape for a product's attribute value (unlike the attribute/family
 * dictionaries, which do return every locale at once). This row therefore seeds the per-locale
 * draft with only the currently-resolved locale; saving sends `translations` for only the
 * locale(s) edited in this session. If the write endpoint fully replaces an attribute's
 * translation set (the contract doesn't document merge-by-locale for this endpoint, unlike e.g.
 * `MeasurementFamilyUpdateRequest.units`), a locale not shown/edited here could be cleared by a
 * save — flagged in user-story-merchant-portal-030's Notes for verification against the live
 * backend.
 */
export function AttributeValueRow({
  productId,
  definition,
  required,
  value,
  languages,
  selectedLocale,
  canManage,
  onNotify,
}: AttributeValueRowProps) {
  const { t, i18n } = useTranslation('catalog');
  const setValueMutation = useSetProductAttributeValue();
  const clearValueMutation = useDeleteProductAttributeValue();
  const measurementFamilyQuery = useMeasurementFamily(definition.measurementFamilyCode, {
    enabled: definition.type === 'MEASUREMENT' && Boolean(definition.measurementFamilyCode),
  });

  // Content-default locale (adr-tenant-005) — preferred over the portal's own UI language when
  // resolving a translated label (see `resolveLocalizedText`'s resolution-order doc); also the
  // locale `buildPayload` mirrors into the flat `value` for a localizable TEXT save, below.
  const defaultLocale = languages.find((l) => l.defaultLocale)?.locale;

  const label = resolveLocalizedText(definition.translations, 'label', i18n.language, definition.code, defaultLocale);

  // Localizable behaviour requires configured catalog languages — with none, a localizable TEXT
  // degrades to the plain single-value field (the flat `value`, no `translations`), mirroring the
  // card-level single-language fallback (`useCardLanguageDraft`'s empty-registry sentinel).
  const effectiveLocalizable =
    definition.type === 'TEXT' && definition.localizable && languages.length > 0;
  // The locale this row's single localizable field edits: the card switcher's selection when it is
  // a known configured language, else the tenant content default, else the first language.
  const activeLocale =
    selectedLocale && languages.some((l) => l.locale === selectedLocale)
      ? selectedLocale
      : (defaultLocale ?? languages[0]?.locale ?? '');
  const activeLanguageName = languages.find((l) => l.locale === activeLocale)?.name ?? activeLocale;

  const [textDraft, setTextDraft] = useState('');
  const [localizedDraft, setLocalizedDraft] = useState<LocalizedFieldValue[]>([]);
  const [numberDraft, setNumberDraft] = useState('');
  const [boolDraft, setBoolDraft] = useState(false);
  const [dateDraft, setDateDraft] = useState('');
  const [selectDraft, setSelectDraft] = useState('');
  const [multiSelectDraft, setMultiSelectDraft] = useState<string[]>([]);
  const [amountDraft, setAmountDraft] = useState('');
  const [unitDraft, setUnitDraft] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);

  // Resync every draft from the server value whenever it changes (initial load, or a fresh
  // ProductDetail after this or another row's save/clear).
  useEffect(() => {
    setFieldError(null);
    const raw = valueAsString(value);
    setTextDraft(definition.type === 'TEXT' && !effectiveLocalizable ? raw : '');
    setLocalizedDraft(
      effectiveLocalizable && value?.resolvedLocale && raw
        ? [{ locale: value.resolvedLocale, text: raw }]
        : [],
    );
    setNumberDraft(definition.type === 'NUMBER' ? raw : '');
    setBoolDraft(definition.type === 'BOOLEAN' ? value?.value === true || raw === 'true' : false);
    setDateDraft(definition.type === 'DATE' ? raw : '');
    setSelectDraft(definition.type === 'SELECT' ? raw : '');
    setMultiSelectDraft(
      definition.type === 'MULTISELECT'
        ? Array.isArray(value?.value)
          ? (value?.value as string[])
          : raw
            ? raw.split(',')
            : []
        : [],
    );
    setAmountDraft(definition.type === 'MEASUREMENT' && value?.amount != null ? String(value.amount) : '');
    setUnitDraft(definition.type === 'MEASUREMENT' ? (value?.unit ?? '') : '');
    // `effectiveLocalizable` is a dep on purpose: the languages registry can resolve AFTER the
    // product value, flipping a localizable TEXT from the plain-field fallback to per-locale mode —
    // the drafts must re-seed into the shape the active mode reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, definition.code, effectiveLocalizable]);

  const dirty = useMemo(() => {
    switch (definition.type) {
      case 'TEXT':
        if (effectiveLocalizable) {
          const baseline =
            value?.resolvedLocale && valueAsString(value)
              ? [{ locale: value.resolvedLocale, text: valueAsString(value) }]
              : [];
          return (
            JSON.stringify([...localizedDraft].sort((a, b) => a.locale.localeCompare(b.locale))) !==
            JSON.stringify([...baseline].sort((a, b) => a.locale.localeCompare(b.locale)))
          );
        }
        return textDraft !== valueAsString(value);
      case 'NUMBER':
        return numberDraft !== (value?.value != null ? String(value.value) : '');
      case 'BOOLEAN':
        return boolDraft !== (value?.value === true);
      case 'DATE':
        return dateDraft !== valueAsString(value);
      case 'SELECT':
        return selectDraft !== valueAsString(value);
      case 'MULTISELECT': {
        const server = Array.isArray(value?.value) ? (value?.value as string[]) : [];
        return sortedJson(multiSelectDraft) !== sortedJson(server);
      }
      case 'MEASUREMENT':
        return (
          amountDraft !== (value?.amount != null ? String(value.amount) : '') || unitDraft !== (value?.unit ?? '')
        );
      default:
        return false;
    }
  }, [
    definition.type,
    effectiveLocalizable,
    textDraft,
    localizedDraft,
    numberDraft,
    boolDraft,
    dateDraft,
    selectDraft,
    multiSelectDraft,
    amountDraft,
    unitDraft,
    value,
  ]);

  const hasServerValue = value !== undefined && value.value !== undefined && value.value !== null;

  const canSave = (() => {
    switch (definition.type) {
      case 'TEXT':
        return effectiveLocalizable
          ? localizedDraft.some((v) => v.text.trim() !== '')
          : textDraft.trim() !== '';
      case 'NUMBER':
        return numberDraft.trim() !== '' && !Number.isNaN(Number(numberDraft));
      case 'BOOLEAN':
        return true;
      case 'DATE':
        return dateDraft.trim() !== '';
      case 'SELECT':
        return selectDraft !== '';
      case 'MULTISELECT':
        return multiSelectDraft.length > 0;
      case 'MEASUREMENT':
        return amountDraft.trim() !== '' && !Number.isNaN(Number(amountDraft)) && unitDraft !== '';
      default:
        return false;
    }
  })();

  const buildPayload = (): AttributeValueSetRequest | null => {
    switch (definition.type) {
      case 'TEXT':
        if (effectiveLocalizable) {
          // Blank drafts are dropped (translations are required non-empty server-side); the flat
          // `value` mirrors the content-default locale's text, falling back to the first entry.
          const entries = localizedDraft.filter((v) => v.text.trim() !== '');
          const defaultEntry = entries.find((v) => v.locale === defaultLocale) ?? entries[0];
          return {
            value: defaultEntry?.text ?? '',
            translations: entries.map((v) => ({ locale: v.locale, value: v.text })),
          };
        }
        return { value: textDraft };
      case 'NUMBER':
        return { value: numberDraft };
      case 'BOOLEAN':
        return { value: boolDraft ? 'true' : 'false' };
      case 'DATE':
        return { value: dateDraft };
      case 'SELECT':
        return { value: selectDraft };
      case 'MULTISELECT':
        return { value: multiSelectDraft.join(',') };
      case 'MEASUREMENT':
        return {
          amount: amountDraft.trim() === '' ? undefined : Number(amountDraft),
          unit: unitDraft || undefined,
        };
      default:
        return null;
    }
  };

  const handleSave = () => {
    const body = buildPayload();
    if (!body) return;
    setFieldError(null);
    setValueMutation.mutate(
      { productId, attributeCode: definition.code, body },
      {
        // No success feedback here beyond the row itself: the mutation's `onProductDetailChanged`
        // already updates the shared `ProductDetail` query cache, which flows back down as this
        // row's `value` prop and clears `dirty` — that state change *is* the save confirmation.
        onError: (error) => {
          if (error.code === 'ATTRIBUTE_VALIDATION_FAILED') {
            const details = readStringDetails(error.details);
            setFieldError(
              t('productAttributesPanel.validationError', {
                rule: details?.rule ?? '—',
                expected: details?.expected ?? '—',
              }),
            );
            return;
          }
          onNotify('error', error.message);
        },
      },
    );
  };

  const handleClear = () => {
    setFieldError(null);
    clearValueMutation.mutate(
      { productId, attributeCode: definition.code },
      {
        onError: (error) => onNotify('error', error.message),
      },
    );
  };

  const pending = setValueMutation.isPending || clearValueMutation.isPending;
  const measurementFamily = measurementFamilyQuery.data;
  const selectedUnit = measurementFamily?.units.find((u) => u.code === unitDraft);
  const normalizedHint =
    measurementFamily && selectedUnit && amountDraft.trim() !== '' && !Number.isNaN(Number(amountDraft))
      ? t('productAttributesPanel.normalizedHint', {
          value: formatNumber(Number(amountDraft) * selectedUnit.conversionFactor),
          unit: measurementFamily.standardUnitCode,
        })
      : null;

  const disabled = !canManage || pending;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
        {/* ── Identity ─────────────────────────────────────────────────── */}
        <Box sx={{ minWidth: { sm: 190 }, flexShrink: 0 }}>
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" fontWeight={600}>
              {label}
            </Typography>
            {required ? (
              <Chip size="small" color="warning" variant="outlined" label={t('productAttributesPanel.requiredChip')} />
            ) : null}
            {value?.inherited ? (
              <Chip
                size="small"
                color="default"
                variant="outlined"
                label={t('inheritance.inheritedFromModel')}
              />
            ) : null}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {t(ATTRIBUTE_TYPE_LABELS[definition.type] ?? definition.type)}
          </Typography>
        </Box>

        {/* ── Control(s) ───────────────────────────────────────────────── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {definition.type === 'TEXT' && effectiveLocalizable ? (
            <Stack spacing={0.75}>
              <TextField
                label={`${t('productAttributesPanel.valueLabel')} — ${activeLanguageName}`}
                value={localizedDraft.find((v) => v.locale === activeLocale)?.text ?? ''}
                onChange={(e) => {
                  const text = e.target.value;
                  setLocalizedDraft((prev) => {
                    const others = prev.filter((v) => v.locale !== activeLocale);
                    // A blanked-out draft is dropped, so that locale is simply omitted on save
                    // (same semantics the stacked editor had).
                    return text.trim() === '' ? others : [...others, { locale: activeLocale, text }];
                  });
                }}
                disabled={disabled}
                fullWidth
                size="small"
                slotProps={{ htmlInput: { maxLength: definition.validation?.maxLength } }}
              />
              <Typography variant="caption" color="text.secondary">
                {hasServerValue &&
                value?.resolvedLocale &&
                value.resolvedLocale !== activeLocale &&
                !localizedDraft.some((v) => v.locale === activeLocale && v.text.trim() !== '')
                  ? t('productAttributesPanel.noValueForSelectedLanguage')
                  : t('productAttributesPanel.localizableHint')}
              </Typography>
            </Stack>
          ) : null}

          {definition.type === 'TEXT' && !effectiveLocalizable ? (
            <TextField
              label={t('productAttributesPanel.valueLabel')}
              value={textDraft}
              onChange={(e) => setTextDraft(e.target.value)}
              disabled={disabled}
              fullWidth
              size="small"
              multiline={!definition.validation?.maxLength || definition.validation.maxLength > 120}
              minRows={definition.validation?.maxLength && definition.validation.maxLength <= 120 ? 1 : 2}
              slotProps={{ htmlInput: { maxLength: definition.validation?.maxLength } }}
            />
          ) : null}

          {definition.type === 'NUMBER' ? (
            <TextField
              label={t('productAttributesPanel.valueLabel')}
              type="number"
              value={numberDraft}
              onChange={(e) => setNumberDraft(e.target.value)}
              disabled={disabled}
              size="small"
              slotProps={{
                htmlInput: { min: definition.validation?.minValue, max: definition.validation?.maxValue, step: 'any' },
              }}
            />
          ) : null}

          {definition.type === 'BOOLEAN' ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                checked={boolDraft}
                onChange={(e) => setBoolDraft(e.target.checked)}
                disabled={disabled}
                slotProps={{ input: { 'aria-label': label } }}
              />
              <Typography variant="body2">
                {boolDraft ? t('productAttributesPanel.booleanYes') : t('productAttributesPanel.booleanNo')}
              </Typography>
            </Stack>
          ) : null}

          {definition.type === 'DATE' ? (
            <TextField
              label={t('productAttributesPanel.valueLabel')}
              type="date"
              value={dateDraft}
              onChange={(e) => setDateDraft(e.target.value)}
              disabled={disabled}
              size="small"
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { min: definition.validation?.minDate, max: definition.validation?.maxDate },
              }}
            />
          ) : null}

          {definition.type === 'SELECT' ? (
            <TextField
              select
              label={t('productAttributesPanel.valueLabel')}
              value={selectDraft}
              onChange={(e) => setSelectDraft(e.target.value)}
              disabled={disabled}
              size="small"
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">
                <em>{t('productAttributesPanel.noSelection')}</em>
              </MenuItem>
              {(definition.options ?? [])
                .filter((o) => o.active || o.code === selectDraft)
                .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                .map((option) => (
                  <MenuItem key={option.code} value={option.code}>
                    {resolveLocalizedText(option.translations, 'label', i18n.language, option.code, defaultLocale)}
                  </MenuItem>
                ))}
            </TextField>
          ) : null}

          {definition.type === 'MULTISELECT' ? (
            <TextField
              select
              label={t('productAttributesPanel.valueLabel')}
              value={multiSelectDraft}
              onChange={(e) => {
                const raw = e.target.value;
                setMultiSelectDraft(typeof raw === 'string' ? raw.split(',') : raw);
              }}
              disabled={disabled}
              size="small"
              sx={{ minWidth: 220 }}
              slotProps={{
                select: {
                  multiple: true,
                  renderValue: (selected) =>
                    (selected as string[])
                      .map((code) => {
                        const option = (definition.options ?? []).find((o) => o.code === code);
                        return option
                          ? resolveLocalizedText(option.translations, 'label', i18n.language, code, defaultLocale)
                          : code;
                      })
                      .join(', '),
                },
              }}
            >
              {(definition.options ?? [])
                .filter((o) => o.active || multiSelectDraft.includes(o.code))
                .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                .map((option) => (
                  <MenuItem key={option.code} value={option.code}>
                    {resolveLocalizedText(option.translations, 'label', i18n.language, option.code, defaultLocale)}
                  </MenuItem>
                ))}
            </TextField>
          ) : null}

          {definition.type === 'MEASUREMENT' ? (
            <Stack spacing={0.75}>
              <Stack direction="row" spacing={1.5}>
                <TextField
                  type="number"
                  label={t('productAttributesPanel.amountLabel')}
                  value={amountDraft}
                  onChange={(e) => setAmountDraft(e.target.value)}
                  disabled={disabled}
                  size="small"
                  sx={{ maxWidth: 160 }}
                  slotProps={{ htmlInput: { step: 'any' } }}
                />
                <TextField
                  select
                  label={t('productAttributesPanel.unitLabel')}
                  value={unitDraft}
                  onChange={(e) => setUnitDraft(e.target.value)}
                  disabled={disabled || measurementFamilyQuery.isLoading}
                  size="small"
                  sx={{ minWidth: 140 }}
                >
                  {(measurementFamily?.units ?? []).map((unit) => (
                    <MenuItem key={unit.code} value={unit.code}>
                      {unit.code}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              {normalizedHint ? (
                <Typography variant="caption" color="text.secondary">
                  {normalizedHint}
                </Typography>
              ) : null}
            </Stack>
          ) : null}

          {fieldError ? (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75 }} role="alert">
              {fieldError}
            </Typography>
          ) : null}
        </Box>

        {/* ── Actions ──────────────────────────────────────────────────── */}
        {canManage ? (
          <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
            {dirty ? (
              <Tooltip title={t('common:action.save')}>
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    aria-label={t('productAttributesPanel.saveAria', { label })}
                    onClick={handleSave}
                    disabled={pending || !canSave}
                  >
                    {setValueMutation.isPending ? <CircularProgress size={16} /> : <SaveRoundedIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            {hasServerValue ? (
              <Tooltip title={t('productAttributesPanel.clearAria', { label })}>
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    aria-label={t('productAttributesPanel.clearAria', { label })}
                    onClick={handleClear}
                    disabled={pending}
                  >
                    {clearValueMutation.isPending ? (
                      <CircularProgress size={16} />
                    ) : (
                      <DeleteOutlineRoundedIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
}
